import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Typography, Alert, CircularProgress, Paper, Snackbar } from '@mui/material';

export const PasswordUpdate: React.FC = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState(false);
  const tokenRef = useRef<HTMLInputElement>(null);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    if (password !== confirm) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    try {
      // Replace with your backend endpoint for password update
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) throw new Error('Failed to reset password');
      setSuccess(true);
      setSnackbar(true);
    } catch (err: any) {
      setError(err.message || 'Error resetting password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
      <Paper sx={{ p: 3, maxWidth: 400, width: '100%' }}>
        <Typography variant="h6" mb={2}>Set New Password</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
        <Box component="form" onSubmit={handleUpdate} aria-label="password update form">
          <TextField
            inputRef={tokenRef}
            label="Reset Token"
            value={token}
            onChange={e => setToken(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="one-time-code"
            aria-label="reset token"
            disabled={loading || success}
          />
          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            aria-label="new password"
            disabled={loading || success}
          />
          <TextField
            label="Confirm Password"
            type="password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            aria-label="confirm password"
            disabled={loading || success}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading || success}
            aria-label="update password"
          >
            {loading ? <CircularProgress size={20} /> : 'Update Password'}
          </Button>
        </Box>
        <Snackbar
          open={snackbar}
          autoHideDuration={3000}
          onClose={() => setSnackbar(false)}
          message="Password updated! You can now log in."
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Paper>
    </Box>
  );
};
